// Spelling Bee: the device says a word, she types it. Works silently too: the sentence is
// shown with the word blanked out.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { makeKeyboard } from '@games/common/keyboard';
import { SPELLING, spellingBandFor, blankOut, type SpellWord } from '@content/words/spelling';

const WORDS_PER_ROUND = 8;

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  const band = spellingBandFor(level);
  const words: SpellWord[] = shuffle(ctx.rng, SPELLING[band]).slice(0, WORDS_PER_ROUND);
  let idx = 0;
  let typed = '';
  let misses = 0;
  let attempts = 0, correct = 0;
  let locked = false;
  const t0 = performance.now();
  let tWord = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Word ', h('strong', null, '1'), ` of ${words.length}`), h('span.muted.small', null, ''));
  const sentence = h('p.center', { style: { fontSize: '19px', fontWeight: '700', minHeight: '52px', padding: '4px 0' } });
  const speakRow = h('div.grid2', null,
    h('button.btn.small', { onclick: () => sayWord() }, 'Say it again'),
    h('button.btn.small', { onclick: () => ctx.speak(cur().sentence) }, 'Use it in a sentence'),
  );
  const typedRow = h('div.row', { style: { justifyContent: 'center', gap: '6px', minHeight: '56px', flexWrap: 'wrap', padding: '4px 0' } });
  const kb = makeKeyboard({
    onKey: k => { if (!locked && typed.length < 16) { typed += k; renderTyped(); } },
    onBackspace: () => { if (!locked) { typed = typed.slice(0, -1); renderTyped(); } },
    onEnter: submit,
    enterLabel: 'Go',
  });
  host.append(head, sentence, speakRow, typedRow, kb.el);

  function cur(): SpellWord { return words[idx]; }

  function sayWord() { ctx.speak(cur().word); }

  function show() {
    (head.querySelector('strong') as HTMLElement).textContent = String(idx + 1);
    (head.lastElementChild as HTMLElement).textContent = `${cur().word.length} letters`;
    sentence.textContent = blankOut(cur());
    typed = ''; misses = 0; locked = false;
    tWord = performance.now();
    renderTyped();
    sayWord();
  }

  /** letter tiles; after a miss, each tile is colored by whether it was right in place */
  function renderTyped(marks?: ('good' | 'bad')[]) {
    clear(typedRow);
    if (!typed && !marks) { typedRow.append(h('span.muted', null, 'Type the word, then tap Go')); return; }
    [...typed].forEach((ch, i) => {
      const m = marks?.[i];
      typedRow.append(h('span', {
        style: {
          minWidth: '34px', height: '48px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '8px', fontWeight: '800', fontSize: '24px', textTransform: 'uppercase', padding: '0 6px',
          background: m === 'good' ? 'var(--good)' : m === 'bad' ? 'var(--line)' : 'var(--surface)',
          color: m === 'good' ? '#fff' : m === 'bad' ? 'var(--text-2)' : 'var(--text)',
          border: '2px solid var(--line)',
        },
      }, ch));
    });
  }

  function submit() {
    if (locked) return;
    const g = typed.trim().toLowerCase();
    if (!g) { ctx.say('Type the word first.'); return; }
    const w = cur().word;
    const ms = performance.now() - tWord;
    if (g === w) {
      locked = true;
      attempts++; correct++;
      ctx.report({ skill: 'phonics_spelling', level, correct: true, hintUsed: misses > 0, ms, problemId: `bee:${w}` });
      ctx.sound('ding');
      renderTyped([...w].map(() => 'good'));
      ctx.say(misses ? `That is it: ${w}.` : `Yes! ${w}.`);
      setTimeout(next, 900);
      return;
    }
    misses++;
    ctx.sound('buzz');
    if (misses === 1) {
      // mark letters right in place; keep what she typed so she can fix it
      const marks = [...g].map((ch, i) => (w[i] === ch ? 'good' : 'bad') as 'good' | 'bad');
      renderTyped(marks);
      const right = marks.filter(m => m === 'good').length;
      ctx.say(right ? `${right} letter${right === 1 ? ' is' : 's are'} in the right place. Green ones stay, fix the gray ones.` : `Not yet. It has ${w.length} letters. Try again.`);
      if (g.length !== w.length) ctx.say(`Not yet. The word has ${w.length} letters, you typed ${g.length}. Green letters are in the right spot.`);
      return;
    }
    // second miss: reveal, count as wrong
    locked = true;
    attempts++;
    ctx.report({ skill: 'phonics_spelling', level, correct: false, hintUsed: true, ms, problemId: `bee:${w}` });
    typed = w;
    renderTyped([...w].map(() => 'good'));
    ctx.say(`It is spelled ${w.toUpperCase().split('').join(' ')}. ${cur().sentence}`);
    ctx.speak(`${w}. ${w.split('').join(', ')}.`);
    setTimeout(next, 1800);
  }

  function next() {
    idx++;
    if (idx >= words.length) {
      if (attempts === correct && attempts >= WORDS_PER_ROUND) ctx.charm('charm.bee8');
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      return;
    }
    level = ctx.currentLevel();
    ctx.say('');
    show();
  }

  show();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.spelling-bee',
  title: 'Spelling Bee',
  blurb: 'Hear a word, spell it. Green letters are in the right place.',
  category: 'words',
  skills: ['phonics_spelling'],
  levelRange: [1, 10],
  unlock: { stars: 4 },
  mount,
};
export default mod;
